import clsx from "clsx";
import { SparklesIcon } from "lucide-react";

import { RichTextDisplay } from "@/components/RichTextDisplay";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
} from "@/lib/constants";
import { CandidacyStatus } from "@/types";

export const CandidacyStatusBadge = ({
  byAgent,
  className,
  fromStatus,
  rejectedReason,
  status,
}: {
  byAgent: boolean;
  className?: string;
  fromStatus?: CandidacyStatus | null;
  rejectedReason?: string | null;
  status: CandidacyStatus;
}) => {
  const hasPrior = !!fromStatus;
  const isRejected = status === CandidacyStatus.REJECTED && hasPrior;
  const isCouldNotContact =
    status === CandidacyStatus.COULD_NOT_CONTACT && hasPrior;
  const isClosed = status === CandidacyStatus.CLOSED && hasPrior;
  const isTerminal = isRejected || isCouldNotContact || isClosed;

  const labelStatus = isTerminal ? fromStatus : status;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="relative">
            <Badge
              className={clsx("gap-2 text-xs", className)}
              variant="outline"
            >
              <div
                className={clsx(
                  "h-3 w-3 rounded-full",
                  candidacyStatusToBackgroundColor[status],
                )}
              />
              <p className={clsx(isTerminal && "text-red-600")}>
                {isClosed && "C - "}
                {isCouldNotContact && "CNC - "}
                {candidacyStatusToLabel[labelStatus]}
              </p>
            </Badge>
            {byAgent && (
              <span className="absolute -right-[7px] -top-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-purple-100 text-purple-600 ring-1 ring-white">
                <SparklesIcon className="h-2 w-2" />
              </span>
            )}
          </span>
        </TooltipTrigger>
        {isRejected && rejectedReason && (
          <TooltipContent
            side="top"
            className="mb-2 max-h-[30vh] max-w-[30vw] overflow-y-auto p-4"
          >
            <RichTextDisplay richText={rejectedReason} />
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
